import React, { useState, useContext } from 'react';
import Modal from './Modal';
import Button from '../ui/Button';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
 import { message } from 'antd';
 import { NecttosContext } from '../../../context/NecttosContext';

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: QuizData) => void;
  subjectId: string;
  modules: any[]
}

interface Question {
  id: string;
  type: 'mcq' | 'text';
  question: string;
  options?: string[];
  correctAnswer?: number | string;
  marks: number;
}

interface QuizData {
  title: string;
  description: string;
  timeLimit: number;
  timeLimitUnit: 'minutes' | 'hours';
  startDate: string;
  endDate: string;
  questions: Question[];
  maxMark: number;
  moduleId: string;
  subjectId: string;
}

const QuizModal: React.FC<QuizModalProps> = ({ isOpen, onClose, onSubmit, subjectId, modules }) => {
  const {collegeId} = useContext(NecttosContext)
  const [formData, setFormData] = useState<QuizData>({
    title: '',
    description: '',
    timeLimit: 60,
    timeLimitUnit: 'minutes',
    startDate: '',
    endDate: '',
    questions: [],
    maxMark: 0,
    moduleId: '',
    subjectId: subjectId
  });

  const [currentQuestion, setCurrentQuestion] = useState<Partial<Question>>({
    type: 'mcq',
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    marks: 1
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calculatemaxMark = (questions: Question[]) => {
    return questions.reduce((total, q) => total + q.marks, 0);
  };

  const validateQuizForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Quiz title is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (formData.questions.length === 0) {
      newErrors.questions = 'At least one question is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateCurrentQuestion = (): boolean => {
    if (!currentQuestion.question?.trim()) {
      alert('Question text is required');
      return false;
    }

    if (currentQuestion.type === 'mcq') {
      const validOptions = currentQuestion.options?.filter(opt => opt.trim()) || [];
      if (validOptions.length < 2) {
        alert('At least 2 options are required for MCQ');
        return false;
      }
    }

    if (!currentQuestion.marks || currentQuestion.marks <= 0) {
      alert('Marks must be greater than 0');
      return false;
    }

    return true;
  };

  const addQuestion = () => {
    if (!validateCurrentQuestion()) return;

    const newQuestion: Question = {
      id: Date.now().toString(),
      type: currentQuestion.type!,
      question: currentQuestion.question!,
      marks: currentQuestion.marks!,
      ...(currentQuestion.type === 'mcq' && {
        options: currentQuestion.options,
        correctAnswer: currentQuestion.correctAnswer
      })
    };

    let updatedQuestions: any[];
    if (editingIndex !== null) {
      updatedQuestions = [...formData.questions];
      updatedQuestions[editingIndex] = newQuestion;
    } else {
      updatedQuestions = [...formData.questions, newQuestion];
    }

    setFormData(prev => ({
      ...prev,
      questions: updatedQuestions,
      maxMark: calculatemaxMark(updatedQuestions)
    }));

    // Reset current question
    setCurrentQuestion({
      type: 'mcq',
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      marks: 1
    });
  };

  const editQuestion = (index: number) => {
    const question = formData.questions[index];
    setCurrentQuestion(question);
    setEditingIndex(index);
  };

  const deleteQuestion = (index: number) => {
    const updatedQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      questions: updatedQuestions,
      maxMark: calculatemaxMark(updatedQuestions)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const instance = await getAxiosTokenInstance();
    if (!validateQuizForm()) {
      return;
    }
  

    setIsSubmitting(true);
    
    try {
      const resp = await instance.post(`/college/lms/quizes/quiz`, {...formData, collegeId: collegeId, teacherId:'TCR-123'});
            if(resp?.data) {
               console.log(resp.data.message)
              message.success('quiz created')
             
            }
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error creating quiz:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      timeLimit: 60,
      timeLimitUnit: 'minutes',
      startDate: '',
      endDate: '',
      questions: [],
      maxMark: 0,
      moduleId:'',
      subjectId: subjectId
    });
    setCurrentQuestion({
      type: 'mcq',
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0,
      marks: 1
    });
    setErrors({});
    setEditingIndex(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Quiz" size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="quizTitle" className="block text-sm font-medium text-gray-700 mb-2">
              Quiz Title *
            </label>
            <input
              type="text"
              id="quizTitle"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter quiz title"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🕐 Time Limit
            </label>
            <div className="flex space-x-2">
              <input
                type="number"
                min="1"
                value={formData.timeLimit}
                onChange={(e) => setFormData(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={formData.timeLimitUnit}
                onChange={(e) => setFormData(prev => ({ ...prev, timeLimitUnit: e.target.value as 'minutes' | 'hours' }))}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
            </div>
          </div>
        </div>


        {/* module */}
        <div>
      <label htmlFor="module" className="block text-sm font-medium text-gray-700 mb-2">
       Module *
      </label>
  <select
    id="module"
    value={formData.moduleId}
    onChange={(e) => setFormData(prev => ({ ...prev, moduleId: e.target.value }))}
    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      errors.module ? 'border-red-500' : 'border-gray-300'
    }`}
  >
    <option value="">Select a module</option>
    {modules?.map((mod) => (
      <option key={mod._id} value={mod._id}>
        {mod.fullModuleName}
      </option>
    ))}
  </select>

  {errors.module && <p className="mt-1 text-sm text-red-600">{errors.module}</p>}
  </div>


        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>
          <textarea
            id="description"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter quiz description..."
          />
        </div>
        

        {/* Availability Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              📅 Start Date *
            </label>
            <input
              type="datetime-local"
              id="startDate"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.startDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              📅 End Date *
            </label>
            <input
              type="datetime-local"
              id="endDate"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.endDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
          </div>
        </div>

        {/* Question Builder */}
        <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingIndex !== null ? 'Edit Question' : 'Add Question'}
          </h3>

          <div className="space-y-4">
            {/* Question Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
              <select
                value={currentQuestion.type}
                onChange={(e) => setCurrentQuestion(prev => ({ 
                  ...prev, 
                  type: e.target.value as 'mcq' | 'text',
                  ...(e.target.value === 'mcq' && { options: ['', '', '', ''], correctAnswer: 0 })
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="mcq">Multiple Choice</option>
                <option value="text">Text Answer</option>
              </select>
            </div>

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question *</label>
              <textarea
                rows={2}
                value={currentQuestion.question}
                onChange={(e) => setCurrentQuestion(prev => ({ ...prev, question: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your question..."
              />
            </div>

            {/* MCQ Options */}
            {currentQuestion.type === 'mcq' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                <div className="space-y-2">
                  {currentQuestion.options?.map((option, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={currentQuestion.correctAnswer === index}
                        onChange={() => setCurrentQuestion(prev => ({ ...prev, correctAnswer: index }))}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(currentQuestion.options || [])];
                          newOptions[index] = e.target.value;
                          setCurrentQuestion(prev => ({ ...prev, options: newOptions }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Option ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Marks */}
            <div className="w-32">
              <label className="block text-sm font-medium text-gray-700 mb-2">Marks</label>
              <input
                type="number"
                min="1"
                value={currentQuestion.marks}
                onChange={(e) => setCurrentQuestion(prev => ({ ...prev, marks: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <Button type="button" onClick={addQuestion} variant="success">
              {editingIndex !== null ? 'Update Question' : '+ Add Question'}
            </Button>
          </div>
        </div>

        {/* Questions List */}
        {formData.questions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Questions</h3>
              <div className="text-sm text-gray-500">
                Total Questions: {formData.questions.length} | Total Marks: {formData.maxMark}
              </div>
            </div>

            <div className="space-y-3">
              {formData.questions.map((question, index) => (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-500">Q{index + 1}.</span>
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {question.type === 'mcq' ? 'Multiple Choice' : 'Text Answer'}
                        </span>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                          {question.marks} marks
                        </span>
                      </div>
                      <p className="text-gray-900 mb-2">{question.question}</p>
                      {question.type === 'mcq' && question.options && (
                        <div className="text-sm text-gray-600">
                          {question.options.map((option, optIndex) => (
                            <div key={optIndex} className={`${optIndex === question.correctAnswer ? 'font-medium text-green-700' : ''}`}>
                              {String.fromCharCode(65 + optIndex)}. {option}
                              {optIndex === question.correctAnswer && ' ✓'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => editQuestion(index)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                      >
                        ✏
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteQuestion(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        ✖
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {errors.questions && <p className="mt-2 text-sm text-red-600">{errors.questions}</p>}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Creating...
              </span>
            ) : (
              'Create Quiz'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default QuizModal;
